export interface EraItem {
  id: string;
  label: string;
  year: string;
  desc: string;
  count: number;
  tags: string[];
  accent: "primary" | "accent" | "secondary" | "foreground";
}

export interface ComicItem {
  id: string;
  title: string;
  titleEn: string;
  year: string;
  era: string;
  type: string;
  episodes: string;
  status: "已补档" | "补档中" | "绝版求补";
  desc: string;
  cover: string;
}

export interface RarePick {
  id: string;
  title: string;
  tag: string;
  note: string;
  cover: string;
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  text: string;
}

export interface FaqItem {
  q: string;
  a: string;
}

export const eras: EraItem[] = [
  {
    id: "era-70",
    label: "1970年代",
    year: "70s",
    desc: "水墨与手绘动画的黄金源头，上海美术电影制片厂的经典源头，老漫画最早的存档刻度。",
    count: 28,
    tags: ["水墨动画", "手绘原稿", "美术片"],
    accent: "secondary",
  },
  {
    id: "era-80",
    label: "1980年代",
    year: "80s",
    desc: "国漫动画井喷年代，诸多童年经典首播，也是老漫画补档最集中、考古价值最高的年代。",
    count: 46,
    tags: ["美术电影", "剪纸动画", "长篇连载"],
    accent: "primary",
  },
  {
    id: "era-90",
    label: "1990年代",
    year: "90s",
    desc: "电视动画普及与引进漫画潮，口袋漫画与杂志连载兴起，绝版资源最多的年代之一。",
    count: 53,
    tags: ["引进老番", "杂志连载", "口袋本"],
    accent: "accent",
  },
  {
    id: "era-00",
    label: "2000年代",
    year: "00s",
    desc: "纸媒连载的尾声与网络补档的起点，许多旧作开始在此年代被封存，等待被重新考古。",
    count: 31,
    tags: ["纸媒尾声", "网络补档", "怀旧番"],
    accent: "foreground",
  },
];

export const featuredComics: ComicItem[] = [
  {
    id: "c-01",
    title: "葫芦兄弟",
    titleEn: "Calabash Brothers",
    year: "1986",
    era: "1980年代",
    type: "国漫动画",
    episodes: "13 集 · 完整版",
    status: "已补档",
    desc: "七个葫芦娃斗蛇精蝎子精的经典连播，一代人的共同记忆，补档含高清修复版与片头片尾。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20hand%20drawn%20animation%20poster%20of%20seven%20colorful%20gourd%20brothers%20heroes%20standing%20together%2C%20retro%201980s%20style%2C%20warm%20amber%20cream%20background%2C%20childhood%20nostalgia&width=600&height=800&seq=comic-hulu-01&orientation=portrait",
  },
  {
    id: "c-02",
    title: "黑猫警长",
    titleEn: "Inspector Black Cat",
    year: "1984",
    era: "1980年代",
    type: "国漫动画",
    episodes: "5 集 · 完整版",
    status: "已补档",
    desc: "森林警官黑猫警长缉拿一只耳，经典警察题材国漫，补档含珍贵幕后设定资料。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20hand%20drawn%20animation%20poster%20of%20a%20heroic%20black%20cat%20police%20officer%20in%20uniform%2C%20retro%201980s%20style%2C%20warm%20amber%20background%2C%20childhood%20nostalgia&width=600&height=800&seq=comic-heimao-02&orientation=portrait",
  },
  {
    id: "c-03",
    title: "大闹天宫",
    titleEn: "Havoc in Heaven",
    year: "1961",
    era: "1970年代",
    type: "经典美术片",
    episodes: "上下集 · 完整版",
    status: "已补档",
    desc: "国产动画丰碑，孙悟空大闹天庭的传世之作，补档提供多版本片源对照与幕后画稿。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20ink%20wash%20animation%20poster%20of%20the%20monkey%20king%20sun%20wukong%20flying%20through%20clouds%20with%20fierce%20eyes%2C%20retro%20art%20style%2C%20warm%20terracotta%20and%20amber%20tones&width=600&height=800&seq=comic-danao-03&orientation=portrait",
  },
  {
    id: "c-04",
    title: "阿凡提的故事",
    titleEn: "The Story of Afanti",
    year: "1979",
    era: "1970年代",
    type: "木偶动画",
    episodes: "14 集 · 完整版",
    status: "已补档",
    desc: "机智维吾尔老人阿凡提智斗巴依老爷的木偶剧，充满生活智慧的年代经典。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20stop%20motion%20puppet%20animation%20poster%20of%20a%20wise%20bearded%20old%20man%20on%20a%20donkey%2C%20retro%20puppet%20film%20style%2C%20warm%20earthy%20amber%20background%2C%20nostalgia&width=600&height=800&seq=comic-afanti-04&orientation=portrait",
  },
  {
    id: "c-05",
    title: "三毛流浪记",
    titleEn: "Sanmao the Vagabond",
    year: "1947",
    era: "1970年代",
    type: "漫画原作",
    episodes: "经典选篇 · 补档",
    status: "补档中",
    desc: "张乐平笔下三根头发的小孤儿，旧上海底层生活的时代写照，漫画原作持续考古修复。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20black%20and%20white%20comic%20style%20poster%20of%20a%20young%20orphan%20boy%20with%20three%20hairs%20of%20hair%20in%20old%20shanghai%2C%20retro%20ink%20drawing%2C%20warm%20sepia%20background%2C%20nostalgia&width=600&height=800&seq=comic-sanmao-05&orientation=portrait",
  },
  {
    id: "c-06",
    title: "哪吒闹海",
    titleEn: "Nezha Conquers the Dragon King",
    year: "1979",
    era: "1970年代",
    type: "经典美术片",
    episodes: "单集 · 完整版",
    status: "已补档",
    desc: "哪吒与东海龙宫的故事，中国动画黄金期的又一高峰，补档含经典配乐与分镜资料。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20hand%20drawn%20animation%20poster%20of%20the%20young%20hero%20nezha%20riding%20a%20flying%20dragon%20above%20a%20raging%20sea%2C%20retro%20art%20style%2C%20warm%20terracotta%20tones%2C%20nostalgia&width=600&height=800&seq=comic-nezha-06&orientation=portrait",
  },
  {
    id: "c-07",
    title: "舒克和贝塔",
    titleEn: "Shuke and Beita",
    year: "1989",
    era: "1980年代",
    type: "国漫动画",
    episodes: "13 集 · 完整版",
    status: "已补档",
    desc: "开飞机的舒克与开坦克的贝塔，两只小老鼠的冒险故事，童年最欢快的动画之一。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20hand%20drawn%20animation%20poster%20of%20two%20cute%20mice%20friends%20one%20flying%20a%20little%20airplane%20and%20one%20driving%20a%20tank%2C%20retro%20style%2C%20warm%20cream%20background%2C%20nostalgia&width=600&height=800&seq=comic-shuke-07&orientation=portrait",
  },
  {
    id: "c-08",
    title: "邋遢大王奇遇记",
    titleEn: "Slovenly King",
    year: "1987",
    era: "1980年代",
    type: "国漫动画",
    episodes: "13 集 · 完整版",
    status: "补档中",
    desc: "不讲卫生的邋遢大王误闯老鼠王国的冒险，朗朗上口的主题曲也是一代记忆。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20hand%20drawn%20animation%20poster%20of%20a%20messy%20haired%20young%20boy%20adventuring%20in%20a%20mysterious%20mouse%20kingdom%2C%20retro%201980s%20style%2C%20warm%20amber%20background%2C%20nostalgia&width=600&height=800&seq=comic-lata-08&orientation=portrait",
  },
];

export const rarePicks: RarePick[] = [
  {
    id: "r-01",
    title: "九色鹿",
    tag: "敦煌美学",
    note: "以敦煌壁画风格绘制的美术片经典，配色与构图的考古价值极高，补档附高清修复。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20dunhuang%20mural%20style%20animation%20poster%20of%20a%20radiant%20nine%20colored%20deer%20with%20flowing%20ribbons%2C%20retro%20mural%20art%2C%20warm%20earth%20tones%2C%20nostalgia&width=600&height=800&seq=rare-jiuse-01&orientation=portrait",
  },
  {
    id: "r-02",
    title: "神笔马良",
    tag: "民间传说",
    note: "一支神笔画出真实世界的故事，民间传说改编的早期经典，老版本资料稀少、补档珍贵。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20hand%20drawn%20animation%20poster%20of%20a%20boy%20with%20a%20magic%20paintbrush%20bringing%20his%20painting%20to%20life%2C%20retro%20folk%20tale%20style%2C%20warm%20amber%20tones%2C%20nostalgia&width=600&height=800&seq=rare-maliang-02&orientation=portrait",
  },
  {
    id: "r-03",
    title: "雪孩子",
    tag: "温情催泪",
    note: "雪人为了救小兔融化自己的催泪经典，画面纯净，是许多人的第一课「离别」。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20hand%20drawn%20animation%20poster%20of%20a%20snowman%20and%20a%20little%20white%20rabbit%20in%20a%20snowy%20forest%2C%20retro%20soft%20style%2C%20cool%20warm%20contrast%20background%2C%20nostalgia&width=600&height=800&seq=rare-xuehai-03&orientation=portrait",
  },
  {
    id: "r-04",
    title: "小蝌蚪找妈妈",
    tag: "水墨之祖",
    note: "中国第一部水墨动画，齐白石画风再现于银幕，老版片源与修复工程都在补档中。",
    cover: "https://readdy.ai/api/search-image?query=vintage%20chinese%20ink%20wash%20animation%20poster%20of%20tiny%20tadpoles%20swimming%20through%20pond%20water%20looking%20for%20mother%2C%20retro%20qibaishi%20ink%20style%2C%20warm%20neutral%20background%2C%20nostalgia&width=600&height=800&seq=rare-kedou-04&orientation=portrait",
  },
];

export const testimonials: Testimonial[] = [
  {
    id: "t-01",
    name: "老周 42岁",
    role: "80后 怀旧读者",
    text: "在这找到小时候看过的《黑猫警长》完整版，还有当年画册的扫描件，一下子就把记忆拉回来了。",
  },
  {
    id: "t-02",
    name: "阿遥 36岁",
    role: "90后 番剧考古",
    text: "按年代归档太省心了，想看哪个年代的经典直接点进去，很多小时候没看完的番终于补上了。",
  },
  {
    id: "t-03",
    name: "子墨 29岁",
    role: "动画研究者",
    text: "做论文找水墨动画资料时发现这里，幕后设定和修复对比都很用心，考古价值超出预期。",
  },
  {
    id: "t-04",
    name: "小满 47岁",
    role: "70后 收藏玩家",
    text: "把压箱底的旧杂志连载整理进补档列表，希望这些绝版资源能一直被人记着。",
  },
];

export const faqs: FaqItem[] = [
  {
    q: "073漫画网的补档资源是按什么年代划分的？",
    a: "补档列表主要按 1970、1980、1990、2000 四个年代归档，同时保留国漫经典、引进老番、绝版资源等专题标签，方便按记忆中的播出年代快速定位那部老作品。",
  },
  {
    q: "哪些漫画属于「童年考古」范畴？",
    a: "只要是你童年里真实出现过、如今在主流平台难寻的老作品——无论是水墨美术片、木偶动画、剪纸动画，还是杂志连载与口袋漫画，都算考古对象，都会尽力收录进补档列表。",
  },
  {
    q: "「绝版求补」是什么意思？怎么参与补档？",
    a: "「绝版求补」表示这部老作品目前缺少清晰片源或完整资料。你可以在投稿区提供扫描件、老磁带或线索，我们审核整理后会将其加入补档列表，并在页面上标注来源。",
  },
  {
    q: "补档的资源清晰度和完整度如何保证？",
    a: "我们优先收录高清修复版，并对同一作品的不同版本做对照与来源标注。部分年代久远的老片源受原始介质限制，我们会如实标注画质状态，绝不擅自伪装成高清。",
  },
  {
    q: "网站可以免费浏览吗？需要注册吗？",
    a: "补档列表与考古资料均免费公开浏览，无需注册。投稿与互动功能会在登录后开放，以方便后续的交流与整理协作。",
  },
];

export const archiveYears: string[] = [
  "1986 葫芦兄弟", "1984 黑猫警长", "1961 大闹天宫", "1979 阿凡提",
  "1947 三毛流浪记", "1979 哪吒闹海", "1989 舒克和贝塔", "1987 邋遢大王",
  "1981 九色鹿", "1983 天书奇谭", "1980 阿凡提·种金子", "1978 人参果",
];